<template>
    <div class="assign-container">
        <span>License</span>
        <div class="section-wrapper">
            <div class="section-container">
                <span>Select</span>
                <div v-if="isLoadingContacts" class="loading-container">
                    <SharedSpinner size="sm" />
                    <span class="loading-text">Loading contacts...</span>
                </div>
                <PrimeSelect v-else class="w-full" v-model="contactSelect" :options="contacts" optionLabel="name" optionValue="id" placeholder="Unassigned" ariaLabel="Assign license to" />
            </div>
        </div>
    </div>
    <div class="assign-footer">
        <div class="footer-actions">
            <prime-button @click="close" variant="secondary" label="Cancel" class="p-btn-root_secondary" autofocus />
            <prime-button label="Assign" variant="primary" @click="assignLicense" :loading="isAssigning" />
        </div>
    </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { getDialogRef } from '~/composables/dynamic-dialog';
import { usePersonStore } from '~/stores/person';
import { useContactsStore } from '~/stores/person/contacts';
import { useLicenseStore } from '~/stores/person/licenses';
import { ModalNames } from '~/types/modals';
import { ContactType, type ContactSelect } from '~/types/person/contacts';

const contactsStore = useContactsStore();
const personStore = usePersonStore();
const dialogRef = getDialogRef(ModalNames.ASSIGN_LICENSE);

const licenseStore = useLicenseStore();
const contactSelect = ref(personStore.selectedPersonForLicense?.assigned_to?.id || null);
const isLoadingContacts = ref(true);
const isAssigning = ref(false);
const { showError } = useGlobalToast();

const contacts = ref<ContactSelect[]>([]);

// Fetch contacts with loading state
const fetchContacts = async () => {
    try {
        isLoadingContacts.value = true;
        await contactsStore.fetchContacts();

        // Populate contacts after fetching
        if (!personStore.person?.pro) {
            contacts.value = [];
            contacts.value.push({
                id: personStore.person?.id!,
                type: ContactType.PERSON,
                name: personStore.person?.name!
            });
        }


        contactsStore.contacts.forEach(contact => {
            if ( contact.licensed) return;
            
            if (contact.trusted_person_id) {
                contacts.value.push({ name: contact.name, id: contact.trusted_person_id, type: ContactType.PERSON });
            } else {
                contacts.value.push({ name: contact.name, id: -contact.id, type: ContactType.CONTACT });
            }
        });
    } catch (error) {
        showError('Error', 'Failed to load contacts');
    } finally {
        isLoadingContacts.value = false;
    }
};

// Call fetchContacts on component mount
onMounted(() => {
    fetchContacts();
});

const close = () => {
    dialogRef.value?.close();
};

const assignLicense = async () => {
    const licenseId = dialogRef.value?.data?.license?.id;
    const contact = contacts.value.find((c) => c.id === contactSelect.value);

    if (!licenseId || !contact) {
        showError('Error', 'Select someone to assign this license to');
        return;
    }

    try {
        isAssigning.value = true;

        // The store assigns from the loaded person's seats and toasts either way.
        if (await licenseStore.assignLicense(licenseId, { ...contact, type: contact.type! })) {
            close();
        }
    } finally {
        isAssigning.value = false;
    }
};
</script>

<style lang="scss" scoped>
.section-wrapper {
    @apply flex flex-col items-center gap-4 self-stretch p-6 px-4 rounded-lg border border-black/10 bg-white;
    // @apply shadow-[0px_1px_3px_rgba(10, 13, 18, 0.10), 0px_1px_2px_-1px_rgba(10, 13, 18, 0.10)];

    .section-container {
        @apply flex flex-col items-start gap-1.5 self-stretch;

        span {
            @apply text-gray-700 text-sm font-medium leading-5;
        }

        .loading-container {
            @apply flex flex-col items-center justify-center gap-3 w-full py-2;

            .loading-text {
                @apply text-gray-500 text-sm font-medium;
            }
        }
    }
}

.assign-container {
    @apply flex flex-col items-start gap-3 px-6 self-stretch overflow-y-auto;

    span {
        @apply text-zinc-900 font-semibold text-sm leading-6;
    }
}

.assign-footer {
    @apply flex flex-col justify-center items-end self-stretch pt-8;

    .footer-actions {
        @apply flex justify-end items-center gap-3 flex-[1_0_0] px-6 pb-6;

        .p-btn-root_primary{
            @apply sm:px-3 px-2 py-2;
        }
    }
}
</style>
